import { useCallback, useMemo } from 'react';
import { useFormContext, useWatch } from 'react-hook-form';
import { useCountriesData } from '@/contexts/CountriesDataProvider';
import { NewLogisticProviderForm } from '../../types';

const ALL_STATES_ITEM = { value: 'All', name: 'All States' };

export const useCoverageSection = (serviceRegionId: string) => {
  const { control, setValue } = useFormContext<NewLogisticProviderForm>();
  const coverage = useWatch({ control, name: 'coverage' });
  const { subdivisions, countries, countriesByIso2, countriesSelectList } = useCountriesData();

  const regionCountryCodes = useMemo(
    () => countries.filter((c) => c.serviceRegionId === serviceRegionId).map((c) => c.alpha2),
    [countries, serviceRegionId],
  );

  const selectedCountryCodes = useMemo(
    () => regionCountryCodes.filter((alpha2) => alpha2 in coverage),
    [regionCountryCodes, coverage],
  );

  const { countrySelectItems, countryNameByAlpha2 } = useMemo(
    () => ({
      countrySelectItems: [
        { value: 'All', name: 'All Countries' },
        ...countriesSelectList.filter(({ value }) => regionCountryCodes.includes(value)),
      ],
      countryNameByAlpha2: Object.fromEntries(
        regionCountryCodes.map((alpha2) => [alpha2, countriesByIso2[alpha2]?.name ?? alpha2]),
      ),
    }),
    [countriesSelectList, countriesByIso2, regionCountryCodes],
  );

  const isAllCurrentlySelected = useMemo(
    () => selectedCountryCodes.length === regionCountryCodes.length && regionCountryCodes.length > 0,
    [selectedCountryCodes, regionCountryCodes],
  );

  const countrySelectedKeys = useMemo(
    () => (isAllCurrentlySelected ? ['All'] : selectedCountryCodes),
    [isAllCurrentlySelected, selectedCountryCodes],
  );

  const subdivisionItemsByAlpha2 = useMemo(
    () =>
      Object.fromEntries(
        selectedCountryCodes.map((alpha2) => [
          alpha2,
          [ALL_STATES_ITEM, ...(subdivisions[alpha2] ?? []).map((s) => ({ value: s.code, name: s.name }))],
        ]),
      ),
    [selectedCountryCodes, subdivisions],
  );

  const handleCountrySelectionChange = useCallback(
    (keys: Iterable<string | number>) => {
      const selected = Array.from(keys) as string[];
      const resolvedKeys = selected.includes('All') ? regionCountryCodes : selected.filter((v) => v !== 'All');
      const regionSet = new Set(regionCountryCodes);

      setValue('coverage', {
        ...Object.fromEntries(Object.entries(coverage).filter(([k]) => !regionSet.has(k))),
        ...Object.fromEntries(resolvedKeys.map((alpha2) => [alpha2, coverage[alpha2] ?? ['All']])),
      });
    },
    [regionCountryCodes, coverage, setValue],
  );

  const handleCountryRemove = useCallback(
    (alpha2: string) => {
      if (alpha2 === 'All') {
        const regionSet = new Set(regionCountryCodes);
        setValue('coverage', Object.fromEntries(Object.entries(coverage).filter(([k]) => !regionSet.has(k))));
      } else {
        const next = { ...coverage };
        delete next[alpha2];
        setValue('coverage', next);
      }
    },
    [coverage, regionCountryCodes, setValue],
  );

  return {
    countrySelectItems,
    countryNameByAlpha2,
    isAllCurrentlySelected,
    countrySelectedKeys,
    subdivisionItemsByAlpha2,
    handleCountrySelectionChange,
    handleCountryRemove,
    regionCountryCodes,
    selectedCountryCodes,
  };
};
